import type { AppAbility } from '@/lib/casl'
import type { QueryClient } from '@tanstack/react-query'
import type { BookedSalesSearch } from '@/lib/services/money/money.schema'
import type { z } from 'zod/v4'
import { SalesReportError } from '@/components/money/SalesReportError'
import { LoadingState } from '@/components/patterns/loading-state'
import { SalesOverview } from '@/components/money/SalesOverview'
import { SalesDailyTable, SalesCashActivity } from '@/components/money/SalesDailyTable'
import { SalesReportActions } from '@/components/money/SalesReportActions'
import {
  createFileRoute,
  notFound,
  redirect,
  useNavigate,
  type SearchSchemaInput,
} from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { BarChart3 } from 'lucide-react'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import { InfoNotice } from '@/components/patterns/info-notice'
import ReportDateRangeFilterBar from '@/components/reports/ReportDateRangeFilterBar'
import { usePathHelper } from '@/hooks/usePathHelper'
import { getErrorMessage } from '@/lib/utils'
import { adminTitleHead } from '@/lib/admin-route-title'
import { canDisplaySalesReport, moneyQueries } from '@/lib/services/money/money.query'
import { bookedSalesFilters, bookedSalesSearchSchema } from '@/lib/services/money/money.schema'
import { getOrganizationNow } from '@/lib/reporting-dates'
import { organizationQueries } from '@/lib/services/organizations/organizations.query'

export async function loadSalesSummaryRoute({
  context,
  params,
  deps,
}: {
  context: {
    ability: Pick<AppAbility, 'can'>
    queryClient: Pick<QueryClient, 'ensureQueryData' | 'prefetchQuery'>
  }
  params: { orgId: number }
  deps: { search: Partial<BookedSalesSearch> }
}) {
  if (!context.ability.can('list', 'transactions')) throw notFound()
  if (+params.orgId === Number(import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID))
    throw redirect({
      to: '/$orgId/reports',
      params: { orgId: params.orgId },
      search: {},
      replace: true,
    })
  const organization = await context.queryClient.ensureQueryData(
    organizationQueries.show(params.orgId)
  )
  const filters = bookedSalesFilters(deps.search, organization.timezone)
  if (filters.day)
    throw redirect({
      to: '/$orgId/reports/sales-summary/$date',
      params: { orgId: params.orgId, date: filters.day },
      search: { start_date: filters.start_date, end_date: filters.end_date },
      replace: true,
    })
  await context.queryClient.prefetchQuery(
    moneyQueries.sales({ organizationId: params.orgId, filters })
  )
  return { filters, timezone: organization.timezone }
}

export const Route = createFileRoute('/_authenticated/$orgId/reports/sales-summary/')({
  component: SalesPage,
  errorComponent: ({ error }) => (
    <InfoNotice tone="warning" title="Sales unavailable">
      {getErrorMessage(error)}
    </InfoNotice>
  ),
  head: () => adminTitleHead('Sales Summary', 'Reports'),
  validateSearch: (search: z.input<typeof bookedSalesSearchSchema> & SearchSchemaInput) =>
    bookedSalesSearchSchema.parse(search),
  loaderDeps: ({ search }): { search: z.output<typeof bookedSalesSearchSchema> } => ({ search }),
  loader: loadSalesSummaryRoute,
})

function SalesPage() {
  const { orgId } = Route.useParams()
  const { filters, timezone } = Route.useLoaderData()
  const {
    data: report,
    error,
    isError,
    isPending,
    isFetching,
    refetch,
  } = useQuery({
    ...moneyQueries.sales({ organizationId: orgId, filters }),
    // The loader already attempted this request; mounting the error UI must not repeat it.
    retryOnMount: false,
  })
  const showReport = canDisplaySalesReport({ data: report, isError, error })
  const path = usePathHelper()
  const navigate = useNavigate()
  const reportNow = getOrganizationNow(timezone)
  const dayLink = (date: string) => ({
    ...path.salesSummary.show(date).using({ orgId }).link,
    search: { start_date: filters.start_date, end_date: filters.end_date },
  })
  return (
    <div className="@container/money space-y-6 pb-6">
      <AdminPageHeader
        icon={BarChart3}
        title="Sales Summary"
        description="Daily sales totals by category and payment method, ready to record as accounting journal entries."
        actions={
          <>
            <ReportDateRangeFilterBar
              now={reportNow}
              initialFilters={{ ...filters, maxDate: reportNow.format('YYYY-MM-DD') }}
              appliedDateMode="custom"
              onApply={(range) =>
                navigate({
                  ...path.salesSummary.index().using({ orgId }).link,
                  search: { start_date: range.start_date, end_date: range.end_date, page: 1 },
                  replace: true,
                })
              }
            />
            {showReport && <SalesReportActions organizationId={orgId} filters={filters} />}
          </>
        }
      />
      {isError && (
        <SalesReportError
          error={error}
          orgId={orgId}
          onRetry={() => refetch()}
          isRetrying={isFetching}
          hasCachedReport={showReport}
        />
      )}
      {isPending ? (
        <LoadingState label="Loading sales…" />
      ) : showReport && report ? (
        <>
          <SalesOverview
            report={report}
            filters={filters}
            onSelectDay={(date) => navigate(dayLink(date))}
          />
          <SalesDailyTable report={report} dayLink={dayLink} />
          <SalesCashActivity report={report} dayLink={dayLink} />
        </>
      ) : null}
    </div>
  )
}
